/*  
<summary>
  Functional Component: TreatmentList
  Developer: Ashutosh Modgil
  Created Date: 22-March-2025
  Description: Renders a Treatment management interface with features for viewing, adding, editing, deleting, and filtering staff. Includes pagination, popups for detailed views, and manual refresh and invalid staff toggles.
</summary>
<returns>JSX for the Treatment Management interface</returns>
*/

import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { observer } from "mobx-react";
import React, {
  useCallback,
  useEffect,
  useState
} from "react";
import toast from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import noMessageIcon from "../../../assets/Images/svg/018-empty.svg";
import { useStore } from "../../../contexts/store-provider";
import { treatmentInitialFilterState } from "../../../core/initialState/initial-filter-state";
import {
  getDefaultActiveKeys,
  resetCategoriesTreatments,
} from "../../../helpers/category-helper";
import { getStoreIdFromLocalStorage } from "../../../helpers/localstorage-helper";
import { ICategory } from "../../../models/state/IStoreTreatment";
import LoaderButton from "../../../shared-components/button/loader-button";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import LanguageSlider from "../../../shared-components/language-slider/language-slider";
import AddPopupHOC from "../../../shared-components/popup/add/add-popup-hoc";
import DeletePopup from "../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../shared-components/popup/delete/delete-popup-hoc";
import ShowPopupHOC from "../../../shared-components/popup/show/show-popup-hoc";
import { formatMessage } from "../../../translations/format-message";
import StoreAddTreatmentManagement from "./components/add-edit-store-treatment";
import SortableCategoryItem from "./components/sortable-category-item";

const StoreTreatmentList: React.FC = (props: any) => {
  const {
    masterManagementStore,
    categoryManagementStore,
    treatmentManagementStore,
  } = useStore();
  const {
    GetCategoryListByStoreIdService,
    UpdateStoreCategoryOrderIdService,
    addUpdateStoreCategoryOrderState,
    allCategoryForTreatmentStore,
    inProgress,
    resetAddUpdateStoreCategoryOrderState,
  } = categoryManagementStore;
  const {
    UpdateStoreTreatmentOrderIdService,
    getTreatmentData,
    GetTreatmentListByCatIdService,
    addUpdateTreatmentManagementState,
    resetAddUpdateTreatmentManagementState,
    deleteTreatmentManagementState,
    resetDeleteTreatmentManagement,
    GetTreatmentListBySearchService,
    setTreatmentStoreFilterDetail,
    resettreatmentStoreFilterDetail,
    getTreatmentStoreFilter,
    clearTreatmentData,
    getTreatmentDataAccordionState,
  } = treatmentManagementStore;
  const {
    GetAllBusinessTypeService,
    getAllBusinessTypes,
    GetAllStoreOptionService,
    getAllStoreOptions,
  } = masterManagementStore;
  const [businessTypeIdSearch, setBusinessTypeIdSearch] = useState(
    treatmentInitialFilterState.BusinessTypeId ?? -1
  );

  const [showInvalidStores, setShowInvalidStores] = useState(
    treatmentInitialFilterState.IsInvalid ?? false
  );

  const [selectedId, setSelectedId] = useState<number>(0);
  const [freeWordSearch, setFreeWordSearch] = useState("");
  const [searchInput, setSearchInput] = useState(freeWordSearch);
  const [openAccordions, setOpenAccordions] = useState<Array<string>>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const getLocalStoreId = getStoreIdFromLocalStorage();

  // Updated state for recursive categories
  const [categories, setCategories] = useState<ICategory[]>(
    allCategoryForTreatmentStore
  );

  const handleSearchClick = useCallback(async () => {
    const searchQuery = searchInput.trim();
    setFreeWordSearch(searchQuery);

    if (searchQuery.length === 0) {
      setOpenAccordions([]);
      resettreatmentStoreFilterDetail();
      setFreeWordSearch("");
      setSearchInput("");
      return;
    }

    setTreatmentStoreFilterDetail({
      IsFilterActive: true,
      Search: searchQuery,
    });

    try {
      // Clear previous search results immediately
      setCategories((prev) => resetCategoriesTreatments(prev));

      await GetTreatmentListBySearchService(searchQuery);

      // Force UI update with new treatment data
      setCategories((prev) => {
        if (getTreatmentData.size === 0) {
          // toast.success(formatMessage("no_data_found"));
          setOpenAccordions([]);
          return [];
        }

        const updateCategories = (cats: ICategory[]): ICategory[] =>
          cats.map((cat) => ({
            ...cat,
            treatments: getTreatmentData.get(Number(cat.categoryId)) || [],
            subCategories: updateCategories(cat.subCategories),
          }));

        const finalUpdatedCategories = updateCategories(
          allCategoryForTreatmentStore
        );
        setOpenAccordions(getDefaultActiveKeys(finalUpdatedCategories));
        return finalUpdatedCategories;
      });
    } catch (error) {
      toast.error(formatMessage("search_failed"));
    }
  }, [
    GetTreatmentListBySearchService,
    allCategoryForTreatmentStore,
    getTreatmentData,
    resettreatmentStoreFilterDetail,
    searchInput,
    setTreatmentStoreFilterDetail,
  ]);

  // Transform the flat category data into a hierarchical structure when received from API
  useEffect(() => {
    // Transform the flat category data into a hierarchical structure
    setCategories(allCategoryForTreatmentStore);
  }, [allCategoryForTreatmentStore]);

  /**
   * This function calls the "getAllGroup" store funtion that get configuration list from API
   */
  const callGetTreatmentManagementListService = useCallback(() => {
    GetCategoryListByStoreIdService(1, getLocalStoreId);
  }, [GetCategoryListByStoreIdService, getLocalStoreId]);

  useEffect(() => {
    callGetTreatmentManagementListService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callGetTreatmentManagementListService]);

  // useEffect(() => {
  //   if (islanguageChanged) {
  //     callGetTreatmentManagementListService();
  //     updateLanguageChangedFlag();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [islanguageChanged]);

  const findCategoryById = useCallback(
    (categories: ICategory[], id: string): ICategory | null => {
      for (const category of categories) {
        if (category.categoryId === id) return category;
        if (category.subCategories && category.subCategories.length > 0) {
          const found = findCategoryById(category.subCategories, id);
          if (found) return found;
        }
      }
      return null;
    },
    []
  );

  const fetchTreatments = useCallback(async () => {
    if (!getTreatmentStoreFilter.IsFilterActive) {
      if (openAccordions.length === 0) return;

      setIsRefreshing(true);
      const fetchPromises = Array.from(openAccordions).map(async (eventKey) => {
        const [, index] = eventKey.split("-");
        const categoryIndex = Number(index);
        // Make sure category exists at this index
        if (categories.length > categoryIndex) {
          const categoryId = categories[categoryIndex].categoryId;
          try {
            await GetTreatmentListByCatIdService(
              Number(categoryId),
              getLocalStoreId
            );
            const treatments = getTreatmentData.get(Number(categoryId)) || [];
            return { categoryId, treatments };
          } catch (error) {
            console.error(
              `Error fetching treatments for category ${categoryId}:`,
              error
            );
          }
        }
        return null;
      });

      const results = await Promise.all(fetchPromises);
      setCategories((prevCategories) => {
        const updated = [...prevCategories];
        results.forEach((result) => {
          if (result) {
            const category = findCategoryById(updated, result.categoryId);
            if (category) {
              category.treatments = result.treatments;
            }
          }
        });
        return updated;
      });
      setIsRefreshing(false);
    }
  }, [
    getTreatmentStoreFilter.IsFilterActive,
    openAccordions,
    categories,
    GetTreatmentListByCatIdService,
    getLocalStoreId,
    getTreatmentData,
    findCategoryById,
  ]);

  const refreshTreatmentsForOpenAccordions = useCallback(async () => {
    if (!getTreatmentStoreFilter.IsFilterActive) {
      if (openAccordions.length === 0) return;

      setIsRefreshing(true);

      const fetchPromises = Array.from(openAccordions).map(async (eventKey) => {
        const [parentCatId, index, catId] = eventKey.split("-");
        const categoryIndex = Number(index);
        // if (categories.length > categoryIndex) {
        // const categoryId = categories[categoryIndex].categoryId;
        const categoryId = catId;
        try {
          await GetTreatmentListByCatIdService(
            Number(categoryId),
            getLocalStoreId
          );
          const treatments = getTreatmentData.get(Number(categoryId)) || [];
          return { categoryId, treatments };
        } catch (error) {
          console.error(
            `Error refreshing treatments for category ${categoryId}:`,
            error
          );
          return { categoryId, treatments: [] }; // Return empty array on error
        }
        // }
        // return null;
      });

      const results = await Promise.all(fetchPromises);

      setCategories((prevCategories) => {
        const updatedCategories = [...prevCategories];

        const traverseUpdatedCategories: (
          categories: ICategory[]
        ) => ICategory[] = (categories: ICategory[]) => {
          return categories.map((cat: ICategory) => {
            const result = results.find(
              (r) => r?.categoryId === cat.categoryId
            );
            // if (result) {
            //   return { ...cat, treatments: result.treatments };
            // }
            cat.treatments = result?.treatments || [];
            if (cat.subCategories.length > 0) {
              return {
                ...cat,
                subCategories: traverseUpdatedCategories(cat.subCategories),
              };
            }
            return cat;
          });
        };
        // results.forEach((result) => {
        //   if (result) {
        //     // updatedCategories[0].subCategories = traverseUpdatedCategories(
        //     //   updatedCategories[0].subCategories
        //     // );
        //     // const categoryIndex = updatedCategories.findIndex(
        //     //   (cat) => cat.categoryId === result.categoryId
        //     // );
        //     // if (categoryIndex !== -1) {
        //     //   updatedCategories[categoryIndex] = {
        //     //     ...updatedCategories[categoryIndex],
        //     //     treatments: result.treatments,
        //     //   };
        //     // } else {
        //     //   if (updatedCategories[0].subCategories.length > 0) {
        //     //   }
        //     //   const subCategoryIndex =
        //     //     updatedCategories[0].subCategories.findIndex(
        //     //       (subCat) => subCat.categoryId === result.categoryId
        //     //     );
        //     //   if (subCategoryIndex !== -1) {
        //     //     updatedCategories[0].subCategories[subCategoryIndex] = {
        //     //       ...updatedCategories[0].subCategories[subCategoryIndex],
        //     //       treatments: result.treatments,
        //     //     };
        //     //   }
        //     // }
        //   }
        // });
        return traverseUpdatedCategories(updatedCategories);
      });

      setIsRefreshing(false);
    }
  }, [
    GetTreatmentListByCatIdService,
    getLocalStoreId,
    getTreatmentData,
    getTreatmentStoreFilter.IsFilterActive,
    openAccordions,
  ]);

  useEffect(() => {
    if (addUpdateTreatmentManagementState.success) {
      refreshTreatmentsForOpenAccordions().then(() => {
        resetAddUpdateTreatmentManagementState();
        // toast.success(formatMessage("treatment_updated_successfully"));
      });
    }
  }, [
    addUpdateTreatmentManagementState.success,
    refreshTreatmentsForOpenAccordions,
    resetAddUpdateTreatmentManagementState,
  ]);

  const findCategoryInSubcategories = useCallback(
    (subCategories: ICategory[], treatmentId: number): string | null => {
      for (const subCategory of subCategories) {
        if (subCategory.treatments?.some((t) => t.Id === treatmentId)) {
          return subCategory.categoryId;
        }
        const found = findCategoryInSubcategories(
          subCategory.subCategories,
          treatmentId
        );
        if (found) return found;
      }
      return null;
    },
    []
  );

  const findCategoryForTreatment = useCallback(
    (treatmentId: number): string | null => {
      for (const category of categories) {
        if (category.treatments?.some((t) => t.Id === treatmentId)) {
          return category.categoryId;
        }
        // Check subcategories recursively
        const foundInSubcategory = findCategoryInSubcategories(
          category.subCategories,
          treatmentId
        );
        if (foundInSubcategory) return foundInSubcategory;
      }
      return null;
    },
    [categories, findCategoryInSubcategories]
  );

  // For delete success
  useEffect(() => {
    if (deleteTreatmentManagementState.success) {
      if (getTreatmentStoreFilter.IsFilterActive) {
        setOpenAccordions([]);
        resettreatmentStoreFilterDetail();
        setFreeWordSearch("");
        setSearchInput("");
      }
      refreshTreatmentsForOpenAccordions().then(() => {
        // Clear the treatment data for the category in the store
        if (selectedId > 0) {
          const categoryId = findCategoryForTreatment(selectedId);
          if (categoryId) {
            getTreatmentData.delete(Number(categoryId));
          }
        }
        resetDeleteTreatmentManagement();
      });
    }
  }, [
    deleteTreatmentManagementState.success,
    findCategoryForTreatment,
    getTreatmentData,
    getTreatmentStoreFilter.IsFilterActive,
    refreshTreatmentsForOpenAccordions,
    resetDeleteTreatmentManagement,
    resettreatmentStoreFilterDetail,
    selectedId,
  ]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateGroupstate.success" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateStoreCategoryOrderState.success) {
      // Don't call callGetTreatmentManagementListService here as it will reset categories
      // Just refresh treatments for currently open accordions
      setTimeout(() => {
        refreshTreatmentsForOpenAccordions();
      }, 10);
      resetAddUpdateStoreCategoryOrderState();
    }
  }, [
    addUpdateStoreCategoryOrderState.success,
    refreshTreatmentsForOpenAccordions,
    resetAddUpdateStoreCategoryOrderState,
  ]);

  const openAddTreatmentManagementHandler = (row: { Id: number }) => {
    setSelectedId(row.Id);
    props.addToggleHandler();
    if (getTreatmentStoreFilter.IsFilterActive) {
      setOpenAccordions([]);
      resettreatmentStoreFilterDetail();
      setFreeWordSearch("");
      setSearchInput("");
    }
  };

  const moveCategory = useCallback(
    (
      parentId: string | null,
      dragIndex: number,
      hoverIndex: number,
      dragCategoryId: number,
      hoverCategoryId: number
    ) => {
      const newCategories = [...categories];
 
      // Store open accordion category IDs (not indices)
      const openCategoryIds = openAccordions.map((key) => {
        const parts = key.split("-");
        return parts[parts.length - 1]; // Get the category ID from the key
      });
 
      // If it's a root category
      if (parentId === null) {
        const [movedCategory] = newCategories.splice(dragIndex, 1);
        newCategories.splice(hoverIndex, 0, movedCategory);

        let draggedCategory = newCategories
          .map((cat) => ({
            ...cat,
            ParentCategoryId: 0
          }))
          .filter(
            (cat) =>
              Number(cat.categoryId) === dragCategoryId
          );
        let droppedCategory = newCategories
          .map((cat) => ({
            ...cat,
            ParentCategoryId: 0
          }))
          .filter(
            (cat) =>
              Number(cat.categoryId) === hoverCategoryId
          );
 
        // Update categories state immediately with treatments preserved
        setCategories(newCategories);
 
        // Rebuild open accordions with new indices
        const newOpenAccordions: string[] = [];
        newCategories.forEach((cat, idx) => {
          if (openCategoryIds.includes(cat.categoryId)) {
            newOpenAccordions.push(`root-${idx}-${cat.categoryId}`);
          }
        });
        setOpenAccordions(newOpenAccordions);
 
        UpdateStoreCategoryOrderIdService([...droppedCategory, ...draggedCategory], getLocalStoreId, 1);
      } else {
        // For nested categories, we need to find the parent
        const findAndUpdateParent = (categories: ICategory[]): ICategory[] => {
          return categories.map((category) => {
            if (category.categoryId === parentId) {
              // Found the parent, update its subcategories
              const newSubCategories = [...category.subCategories];
              const [movedCategory] = newSubCategories.splice(dragIndex, 1);
              newSubCategories.splice(hoverIndex, 0, movedCategory);

              return {
                ...category,
                subCategories: newSubCategories,
              };
            }

            if (category.subCategories.length > 0) {
              return {
                ...category,
                subCategories: findAndUpdateParent(category.subCategories),
              };
            }

            return category;
          });
        };

        let droppedSubCategory = findAndUpdateParent(newCategories);
 
        // Update categories state with treatments preserved
        setCategories(droppedSubCategory);
 
        let draggedCategory = findCategoryById(
          droppedSubCategory,
          String(dragCategoryId)
        );
        let hoveredCategory = findCategoryById(
          droppedSubCategory,
          String(hoverCategoryId)
        );

        // let draggedCatOrderId = draggedCategory!.orderId;
        // draggedCategory!.orderId = hoveredCategory!.orderId;
        // hoveredCategory!.orderId = draggedCatOrderId;
        let finalDroppedSubCategory = [{...hoveredCategory, ParentCategoryId: parentId} , {...draggedCategory, ParentCategoryId: parentId}];
 
        // Rebuild open accordions for subcategories
        const rebuildAccordionKeys = (
          cats: ICategory[],
          parentKey: string = "root"
        ): void => {
          cats.forEach((cat, idx) => {
            const currentKey = `${parentKey}-${idx}-${cat.categoryId}`;
            if (openCategoryIds.includes(cat.categoryId)) {
              const keyIndex = openAccordions.findIndex((k) =>
                k.endsWith(`-${cat.categoryId}`)
              );
              if (keyIndex !== -1) {
                openAccordions[keyIndex] = currentKey;
              }
            }
            if (cat.subCategories.length > 0) {
              rebuildAccordionKeys(cat.subCategories, currentKey);
            }
          });
        };
 
        rebuildAccordionKeys(droppedSubCategory);
        setOpenAccordions([...openAccordions]);
 
        UpdateStoreCategoryOrderIdService(
          finalDroppedSubCategory,
          getLocalStoreId,
          1
        );
      }
    },
    [
      UpdateStoreCategoryOrderIdService,
      categories,
      getLocalStoreId,
      openAccordions,
      setOpenAccordions,
      findCategoryById,
      setCategories,
    ]
  );

  
  const moveItem = useCallback(
    (
      dragIndex: number,
      hoverIndex: number,
      sourceCategoryId: string,
      targetCategoryId: string,
      dragTreatmentId: number,
      hoverTreatmentId: number
    ) => {
      if (sourceCategoryId !== targetCategoryId) return;

      const prevCategories = [...categories];
      const updateCategoryTreatments = (category: ICategory[]): ICategory[] => {
        return category.map((cat) => {
          if (cat.categoryId === sourceCategoryId) {
            // Create new array with moved treatment
            const newTreatments = [...(cat.treatments || [])];
            const [movedItem] = newTreatments.splice(dragIndex, 1);
            newTreatments.splice(hoverIndex, 0, movedItem);
            return { ...cat, treatments: newTreatments };
          }
          if (cat.subCategories.length > 0) {
            return {
              ...cat,
              subCategories: updateCategoryTreatments(cat.subCategories),
            };
          }
          return cat;
        });
      };

      let updatedTreatments = updateCategoryTreatments([...prevCategories]);
      let finalDraggedTreatment = [];
      let finalDroppedTreatment = [];
      // Find the category (could be at any level)
      const sourceCategory = findCategoryById(
        updatedTreatments,
        sourceCategoryId
      );

      if (sourceCategory) {
        finalDraggedTreatment = sourceCategory.treatments
          .map((treatment) => ({
            ...treatment,
            CategoryId: Number(sourceCategoryId),
          }))
          .filter(
            (treatment) =>
              Number(treatment.Id) === dragTreatmentId
          );
        finalDroppedTreatment = sourceCategory.treatments
          .map((treatment) => ({
            ...treatment,
            CategoryId: Number(sourceCategoryId),
          }))
          .filter(
            (treatment) =>
              Number(treatment.Id) === hoverTreatmentId
          );
        UpdateStoreTreatmentOrderIdService(
          [...finalDroppedTreatment, ...finalDraggedTreatment],
          getLocalStoreId
        );
      }
    },
    [
      categories,
      findCategoryById,
      UpdateStoreTreatmentOrderIdService,
      getLocalStoreId,
    ]
  );


  const handleDetailClick = (treatment: any) => {
    openAddTreatmentManagementHandler({ Id: treatment.Id });
  };

  useEffect(() => {
    if (searchInput === "" && getTreatmentStoreFilter.IsFilterActive) {
      resettreatmentStoreFilterDetail();
      setOpenAccordions([]);
      callGetTreatmentManagementListService();
    }
  }, [
    callGetTreatmentManagementListService,
    getTreatmentStoreFilter.IsFilterActive,
    resettreatmentStoreFilterDetail,
    searchInput,
  ]);

  return (
    <React.Fragment>
      <FormLoader
        loading={
          addUpdateStoreCategoryOrderState.inProgress ||
          inProgress ||
          isRefreshing
        }
      />

      {props.showAddPopup && (
        <StoreAddTreatmentManagement
          id={selectedId}
          modalClosed={props.addToggleHandler}
          storeId={getLocalStoreId}
        />
      )}
      {props.showDeletePopup && (
        <DeletePopup
          title="delete_User"
          modalSubmit={() => {}}
          modalClosed={props.deletePopupToggleHandler}
          message="message_delete_user_confirm"
          isLoading={false}
        />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="treatment_management" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="store_management" />
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="treatment_management" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid mt-4 p-0">
        <div className="row mb-2">
          <div className="col-6 search_box">
            <div className="row mb-3">
              <div className="col-xl-4 col-sm-8 col-12">
                <div className="input-group">
                  <div className="input-group-text">
                    <FontAwesomeIcon
                      icon={faSearch}
                      onClick={handleSearchClick}
                    />
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={formatMessage("search_by_free_word")}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSearchClick();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-6">
            <div className="d-flex justify-content-end align-items-center">
              {getTreatmentStoreFilter.IsFilterActive && (
                <button
                  className={`btn ${
                    getTreatmentStoreFilter.IsFilterActive
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => {
                    resettreatmentStoreFilterDetail();
                    setFreeWordSearch("");
                    setSearchInput("");
                    setOpenAccordions([]);
                    callGetTreatmentManagementListService();
                  }}
                  title={formatMessage(
                    getTreatmentStoreFilter.IsFilterActive
                      ? "clear_filter"
                      : "apply_filter"
                  )}
                >
                  <svg
                    id="fi_3126668"
                    enableBackground="new 0 0 512 512"
                    height="18"
                    viewBox="0 0 512 512"
                    width="18"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="#ffffff"
                  >
                    <g>
                      <path d="m215.546 85.327h-162.264c-18.073 0-28.679 20.379-18.31 35.187.133.199-3.448-4.682 130.024 177.006 5.921 8.587 4.149-.599 4.149 190.987 0 19.245 21.993 30.358 37.542 18.791 57.536-43.372 71.516-48.257 71.516-70.955 0-133.909-1.721-130.311 4.149-138.823l27.851-37.923c-70.082-25.496-112.087-99.608-94.657-174.27z"></path>
                      <path d="m281.951 30.166c-75.076 67.31-38.685 187.35 55.962 206.05 75.479 15.948 143.193-43.867 143.193-116.945 0-102.594-122.364-157.159-199.155-89.105zm118.529 106.804c9.515 9.466 2.715 25.676-10.603 25.676-8.014 0-10.022-3.79-28.462-22.158-18.064 17.984-20.27 22.158-28.472 22.158-13.349 0-20.063-16.264-10.603-25.676l17.769-17.699-17.769-17.699c-14.107-14.035 7.142-35.322 21.216-21.297l17.859 17.779 17.849-17.779c14.074-14.025 35.331 7.254 21.216 21.297l-17.769 17.699z"></path>
                    </g>
                  </svg>
                </button>
              )}
              {/* 
              ADDED REFRESH BUTTON FOR TESTING WILL REMOVE IT AFTER FINAL COMMIT , FOR NOW COMMITING IT FOR FUTURE ID MISMATCH ISSUE
              {openAccordions.size > 0 && (
                <button
                  className="btn btn-outline-secondary me-2"
                  onClick={handleManualRefresh}
                  disabled={isRefreshing}
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  {formatMessage("refresh")}
                </button>
              )} */}
              <button
                className="btn btn-primary ms-3"
                onClick={() => {
                  openAddTreatmentManagementHandler({ Id: -1 });
                }}
              >
                <i className="bi bi-plus-lg me-1"></i>
                {formatMessage("add_treatment")}
              </button>
            </div>
          </div>
        </div>
        {categories.length > 0 ? (
          <>
            {categories?.map((category, index) => {
              return (
                // <Accordion alwaysOpen defaultActiveKey={[`root-0`, `root-1`]}>
                <SortableCategoryItem
                  key={category.categoryId}
                  category={category}
                  index={index}
                  parentId={null}
                  moveCategory={moveCategory}
                  moveItem={moveItem}
                  onDetailClick={handleDetailClick}
                  setCategories={setCategories}
                  storeId={getLocalStoreId}
                  openAccordions={openAccordions}
                  setOpenAccordions={setOpenAccordions}
                />
              );
            })}
          </>
        ) : (
          <div className="message text-center">
            <img src={noMessageIcon} alt="" className="no_message" />
            {freeWordSearch.trim().length > 0 || showInvalidStores ? (
              <>
                <p className="noData text-muted text-center h4 mb-0">
                  <FormattedMessage id="no_data_available" />
                </p>
              </>
            ) : (
              <>
                {getLocalStoreId > 0 ? (
                  <>
                    <p className="noData text-muted text-center h4 mb-0">
                      <FormattedMessage id="no_treatment" />
                    </p>
                    <p className="mb-3">
                      {formatMessage("no_treatment_message")}
                    </p>
                  </>
                ) : (
                  <p className="mb-3">{formatMessage("please_select_store")}</p>
                )}
                <LoaderButton
                  text="add_treatment"
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                    openAddTreatmentManagementHandler({ Id: -1 })
                  }
                />
              </>
            )}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default AddPopupHOC(
  ShowPopupHOC(DeletePopupHOC(observer(StoreTreatmentList)))
);
